import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { serviceService, adminService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedEnglish, setExpandedEnglish] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await serviceService.getAll();
      // Initialize includedEn if not present
      const servicesWithEn = response.data.map(service => ({
        ...service,
        includedEn: service.includedEn || service.included.map(() => '')
      }));
      setServices(servicesWithEn);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({
        title: '錯誤',
        description: '無法載入服務資料',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (serviceId, index) => {
    setSaving(true);
    try {
      const serviceData = services[index];
      
      console.log('Saving service:', serviceId, serviceData);
      
      const response = await adminService.services.update(serviceId, {
        type: serviceData.type,
        title: serviceData.title,
        titleEn: serviceData.titleEn || '',
        filipinoPrice: serviceData.filipinoPrice,
        indonesianPrice: serviceData.indonesianPrice,
        included: serviceData.included,
        includedEn: serviceData.includedEn || []
      });
      
      console.log('Save response:', response);
      
      toast({
        title: '成功 / Success',
        description: '服務已更新 / Service updated'
      });
      
      // Refresh services to show updated data
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      console.error('Error details:', error.response?.data);
      toast({
        title: '錯誤',
        description: error.response?.data?.detail || '保存失敗，請再試一次',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const updateService = (index, field, value) => {
    const newServices = [...services];
    newServices[index][field] = value;
    setServices(newServices);
  };

  const updateIncluded = (index, includedIndex, value) => {
    const newServices = [...services];
    newServices[index].included[includedIndex] = value;
    setServices(newServices);
  };

  const updateIncludedEn = (index, includedIndex, value) => {
    const newServices = [...services];
    if (!newServices[index].includedEn) {
      newServices[index].includedEn = newServices[index].included.map(() => '');
    }
    newServices[index].includedEn[includedIndex] = value;
    setServices(newServices);
  };

  const addIncludedItem = (index) => {
    const newServices = [...services];
    newServices[index].included.push('');
    if (!newServices[index].includedEn) {
      newServices[index].includedEn = [];
    }
    newServices[index].includedEn.push('');
    setServices(newServices);
  };

  const removeIncludedItem = (index, includedIndex) => {
    const newServices = [...services];
    newServices[index].included.splice(includedIndex, 1);
    if (newServices[index].includedEn) {
      newServices[index].includedEn.splice(includedIndex, 1);
    }
    setServices(newServices);
  };

  const toggleEnglishSection = (index) => {
    setExpandedEnglish(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>服務價格管理 / Service Price Management</h1>
        <p className="text-gray-600 mt-2">編輯所有服務類型的價格和內容（中英文）/ Edit all service types, prices and content (Chinese & English)</p>
      </div>

      <div className="space-y-6">
        {services.map((service, index) => (
          <Card key={service.id}>
            <CardHeader style={{ backgroundColor: '#f8fffe' }}>
              <CardTitle className="text-xl" style={{ color: '#00b6a5' }}>
                {service.title} {service.titleEn && `/ ${service.titleEn}`}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Service Title */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">服務名稱（中文）* Chinese Title</label>
                  <Input
                    value={service.title}
                    onChange={(e) => updateService(index, 'title', e.target.value)}
                    placeholder="例如：海外傭工"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">服務名稱（英文）English Title</label>
                  <Input
                    value={service.titleEn || ''}
                    onChange={(e) => updateService(index, 'titleEn', e.target.value)}
                    placeholder="e.g., Overseas Helper"
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">菲律賓女傭價格 * Filipino Price</label>
                  <Input
                    value={service.filipinoPrice}
                    onChange={(e) => updateService(index, 'filipinoPrice', e.target.value)}
                    placeholder="例如：港幣 $15,800起"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">印尼女傭價格 * Indonesian Price</label>
                  <Input
                    value={service.indonesianPrice}
                    onChange={(e) => updateService(index, 'indonesianPrice', e.target.value)}
                    placeholder="例如：港幣 $15,800起"
                  />
                </div>
              </div>

              {/* Included Items - Chinese */}
              <div>
                <label className="block text-sm font-medium mb-2">收費包括項目（中文）* Included Items (Chinese)</label>
                <div className="space-y-2">
                  {service.included.map((item, includedIndex) => (
                    <div key={includedIndex} className="flex gap-2">
                      <span className="flex items-center text-gray-500 w-6">{includedIndex + 1}.</span>
                      <Input
                        value={item}
                        onChange={(e) => updateIncluded(index, includedIndex, e.target.value)}
                        placeholder="例如：海外驗身"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => removeIncludedItem(index, includedIndex)}
                      >
                        刪除
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addIncludedItem(index)}
                    style={{ borderColor: '#00b6a5', color: '#00b6a5' }}
                  >
                    + 新增項目 / Add Item
                  </Button>
                </div>
              </div>

              {/* English Section Toggle */}
              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => toggleEnglishSection(index)}
                  className="w-full flex items-center justify-between"
                  style={{ borderColor: '#00b6a5', color: '#00b6a5' }}
                >
                  <span>📝 English Translations / 英文翻譯</span>
                  {expandedEnglish[index] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>

              {/* Included Items - English (Expandable) */}
              {expandedEnglish[index] && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium mb-2 text-blue-800">
                    Included Items (English) / 收費包括項目（英文）
                  </label>
                  <p className="text-xs text-blue-600 mb-3">Enter English translations for each item above</p>
                  <div className="space-y-2">
                    {service.included.map((item, includedIndex) => (
                      <div key={includedIndex} className="flex gap-2 items-center">
                        <span className="flex items-center text-gray-500 w-6">{includedIndex + 1}.</span>
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <Input
                            value={item}
                            disabled
                            className="bg-gray-100 text-gray-500"
                            placeholder="Chinese"
                          />
                          <Input
                            value={service.includedEn?.[includedIndex] || ''}
                            onChange={(e) => updateIncludedEn(index, includedIndex, e.target.value)}
                            placeholder="English translation"
                            className="border-blue-300"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="pt-4 pb-8">
                <Button
                  onClick={() => handleSave(service.id, index)}
                  disabled={saving}
                  style={{ backgroundColor: '#00b6a5' }}
                  className="text-white"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  保存更改 / Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminServices;
